import { type ReactNode, useEffect, useRef, useState } from 'react';

import { Box, ButtonBase, useTheme } from '@mui/material';
import scrollbarSize from 'dom-helpers/scrollbarSize';

const MAX_SCROLL_BAR_SIZE = 32;
const FILTER_SCROLL_DISTANCE = 150;

const HorizontalScrollContainer = ({
  height,
  children,
  nodeIndex,
}: {
  nodeIndex: number;
  height: number;
  children: ReactNode;
}) => {
  const { palette } = useTheme();
  const [showScrollLeft, setShowScrollLeft] = useState(false);
  const [showScrollRight, setShowScrollRight] = useState(false);
  const menuContainerRef = useRef<HTMLElement | null>(null);
  const menuRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const firstChildObserver = new IntersectionObserver(
      ([entry]) => {
        setShowScrollLeft(!entry.isIntersecting);
      },
      { threshold: 0.7 },
    );

    const lastChildObserver = new IntersectionObserver(
      ([entry]) => {
        setShowScrollRight(!entry.isIntersecting);
      },
      { threshold: 0.7 },
    );

    const firstChildElement = menuRef.current?.firstElementChild;
    const lastChildElement = menuRef.current?.lastElementChild;

    if (firstChildElement != null && lastChildElement != null) {
      firstChildObserver.observe(firstChildElement);
      lastChildObserver.observe(lastChildElement);

      return () => {
        firstChildObserver.unobserve(firstChildElement);
        lastChildObserver.unobserve(lastChildElement);
      };
    }
  }, [menuRef]);

  useEffect(() => {
    const node = menuRef.current?.children[nodeIndex];
    node?.scrollIntoView({ block: 'end' });
  }, [menuRef, nodeIndex]);

  const baseArrowButtonStyles = {
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    top: 0,
    bottom: 0,
    width: 36,
  };

  return (
    <div
      css={{ overflow: 'hidden', position: 'relative', height: `${height}px` }}
    >
      <Box
        display="flex"
        ref={menuContainerRef}
        alignItems="center"
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'smooth',
          height: '100%',
          boxSizing: 'content-box',
          paddingBottom: `${scrollbarSize() || MAX_SCROLL_BAR_SIZE}px`,
        }}
      >
        <Box display="flex" ref={menuRef} pr={3} sx={{ minWidth: '100%' }}>
          {children}
        </Box>
      </Box>
      {showScrollLeft && (
        <ButtonBase
          onClick={() => {
            if (menuContainerRef.current) {
              menuContainerRef.current.scrollBy(-FILTER_SCROLL_DISTANCE, 0);
            }
          }}
          sx={{
            ...baseArrowButtonStyles,
            background:
              'linear-gradient(90deg, rgba(246,247,249,1) 70%, rgba(246,247,249,0) 100%)',
            left: 0,
          }}
        >
          <Box
            css={{
              width: 0,
              height: 0,
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderRight: `6px solid ${palette.text.secondary}`,
            }}
          />
        </ButtonBase>
      )}
      {showScrollRight && (
        <ButtonBase
          onClick={() => {
            if (menuContainerRef.current) {
              menuContainerRef.current.scrollBy(FILTER_SCROLL_DISTANCE, 0);
            }
          }}
          sx={{
            ...baseArrowButtonStyles,
            background:
              'linear-gradient(270deg, rgba(246,247,249,1) 70%, rgba(246,247,249,0) 100%)',
            borderRight: `1px solid ${palette.grey[300]}`,
            right: 0,
          }}
        >
          <div
            css={{
              width: 0,
              height: 0,
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderLeft: `6px solid ${palette.text.secondary}`,
            }}
          />
        </ButtonBase>
      )}
    </div>
  );
};

export default HorizontalScrollContainer;
